import { usePageConditionAtomValue } from '@/features/analyze/atoms/pageCondition.atom';
import { useArticleSearchCondition } from '@/features/analyze/hooks/useArticleSearchCondition';
import { filterTreemapLabels } from '@/features/analyze/lib/filterTreemapLabels';
import { graphql, ResultOf, useQuery } from '@/lib/graphql';
import { useState } from 'react';
import { GroupItem } from '../components/FoamTreeView';

export const GET_TREEMAP_DATA_QUERY = graphql(`
  query getTreemapData(
    $condition: ArticleSearchConditionInput!
    $targetArticleCount: Int!
  ) {
    treemapData(
      condition: $condition
      targetArticleCount: $targetArticleCount
    ) {
      clusters {
        clusterLabel
        weight
        articleIds
      }
      articles {
        id
        code
        title
        url
        summary
        publishedAt
        country
        countryTags
        organizationType
        organization
        displayableTopicTags {
          lookupKey
          name
          description
          themes
          isSocialIssue
        }
        themes
        people
        companies
        locations
        imageUrl
        isBookmarkedByCurrentUser
        bodyPreview
      }
    }
  }
`);

export type ActiveArticle = ResultOf<
  typeof GET_TREEMAP_DATA_QUERY
>['treemapData']['articles'][number];

export const useAnalyzeClustering = () => {
  const [selectedArticleId, setSelectedArticleId] = useState<string>();
  const condition = useArticleSearchCondition();
  const pageCondition = usePageConditionAtomValue();

  const { data, loading, error } = useQuery(GET_TREEMAP_DATA_QUERY, {
    variables: {
      condition,
      targetArticleCount: pageCondition.perPage || 50,
    },
    errorPolicy: 'all',
  });

  if (error) {
    console.error('GraphQL Error in useAnalyzeClustering:', error);
  }

  const onSelectTarget = (e: { group?: Partial<GroupItem> }) => {
    const target = e?.group?.doc;
    if (!target) return;
    setSelectedArticleId(target.id);
  };

  const selectedArticle = data?.treemapData?.articles?.find(
    (article) => article.id === selectedArticleId
  );

  const formattedTreemapData =
    data?.treemapData.clusters
      ?.map((cluster) => {
        const filteredLabels = filterTreemapLabels([cluster.clusterLabel]);
        if (filteredLabels.length === 0) return null; // フィルタリング結果が空の場合は除外

        return {
          label: filteredLabels[0],
          weight: cluster.weight,
          groups: cluster.articleIds
            .map((articleId) => {
              const article = data.treemapData.articles.find(
                (a) => a.id === articleId
              );
              return article
                ? { id: article.id, label: article.title, doc: article }
                : null;
            })
            .filter(Boolean),
        };
      })
      .filter(Boolean) || [];

  return {
    formattedTreemapData,
    selectedArticle,
    onSelectTarget,
    isLoading: loading,
  };
};
